import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookOpen, Sparkles, Calendar, X } from "lucide-react";

const ExplorationChoiceModal = ({
  isOpen,
  onClose,
  onContinue,
  onStartFresh,
  question,
  explorationData,
}) => {
  if (!explorationData) return null;

  // Handle both IndexedDB (nodes array) and Firestore (nodeCount or _firestoreNodes)
  const nodeCount =
    explorationData.nodes?.length ||
    explorationData.nodeCount ||
    (explorationData._firestoreNodes ? Object.keys(explorationData._firestoreNodes).length : 0);

  const lastModified = explorationData.lastModified || explorationData.updatedAt;

  // Debug logging
  console.log('📊 ExplorationChoiceModal - explorationData:', {
    name: explorationData.name,
    hasNodes: !!explorationData.nodes,
    nodesLength: explorationData.nodes?.length,
    hasNodeCount: !!explorationData.nodeCount,
    nodeCountValue: explorationData.nodeCount,
    hasFirestoreNodes: !!explorationData._firestoreNodes,
    firestoreNodesCount: explorationData._firestoreNodes ? Object.keys(explorationData._firestoreNodes).length : 0,
    calculatedNodeCount: nodeCount,
    lastModified,
  });

  // Format last modified date
  const formatLastModified = (dateString) => {
    if (!dateString) return "recently";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "today";
    if (diffDays === 2) return "yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;
    if (diffDays <= 30) return `${Math.floor((diffDays - 1) / 7)} weeks ago`;

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={onClose}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                  <BookOpen size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-800">
                    Continue Exploration?
                  </h3>
                  <p className="text-sm text-gray-500">
                    You've explored this before
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                <X size={16} />
              </button>
            </div>

            {/* Exploration Info */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 mb-6 border border-blue-100">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-2">
                    "{question}"
                  </h4>
                  <div className="flex items-center gap-2 text-xs text-gray-600">
                    <Calendar size={12} />
                    <span>
                      Last explored {formatLastModified(lastModified)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 bg-blue-100 rounded flex items-center justify-center">
                    <span className="text-xs">💡</span>
                  </div>
                  <span className="font-medium text-blue-700">{nodeCount}</span>
                  <span className="text-gray-600 text-xs">insights</span>
                </div>

                {explorationData.booksMentioned?.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 bg-amber-100 rounded flex items-center justify-center">
                      <span className="text-xs">📚</span>
                    </div>
                    <span className="font-medium text-amber-700">
                      {explorationData.booksMentioned.length}
                    </span>
                    <span className="text-gray-600 text-xs">books</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={onContinue}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium shadow-lg">
                <BookOpen size={16} />
                Continue Previous Exploration
              </button>

              <button
                onClick={onStartFresh}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium">
                <Sparkles size={16} />
                Create New Exploration
              </button>
            </div>

            {/* Footer Note */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500">
                💡 Your previous exploration is always safe - nothing gets
                overwritten
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExplorationChoiceModal;
